/**
 * @file Связь экрана с текущей папкой и файловыми операциями.
 *
 * Хранит навигацию отдельно для каждого хранилища, читает список и обновляет
 * его после успешного изменения. Ушедший с экрана запрос не должен перезаписать
 * результат новой папки; повторная операция над занятым элементом блокируется.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { FileOperationError, validFolderPath } from '../features/files/model'
import { createFolder } from '../features/folders/api'
import { rememberFolder } from '../features/transfers/snapshots'
import {
    type FolderContent,
    deleteFile,
    deleteFolder,
    fetchFolderContent,
    renameFile,
} from '../services/box_api'

type Params = { boxID: string; token?: string; connected: boolean }

function storedFolder(boxID: string) {
    try {
        return validFolderPath(localStorage.getItem(`currentFolder:${boxID}`))
    } catch {
        return '/home'
    }
}

export function useFolder({ boxID, token, connected }: Params) {
    const [selection, setSelection] = useState(() => ({
        boxID,
        path: storedFolder(boxID),
    }))

    const currentFolder =
        selection.boxID === boxID ? selection.path : storedFolder(boxID)

    const setCurrentFolder = useCallback(
        (path: string) => setSelection({ boxID, path: validFolderPath(path) }),
        [boxID],
    )

    useEffect(() => {
        try {
            localStorage.setItem(`currentFolder:${boxID}`, currentFolder)
        } catch {
            /* Недоступный localStorage не должен мешать навигации. */
        }
    }, [boxID, currentFolder])

    // Ref блокирует вторую отправку сразу, ещё до следующего отображения React.
    // pendingKeys ниже нужен отдельно: его изменение обновляет кнопки на экране.
    const pending = useRef(new Set<string>())
    const [pendingKeys, setPendingKeys] = useState<ReadonlySet<string>>(
        new Set(),
    )

    const mutationKey = useCallback(
        (name: string) => JSON.stringify([boxID, `${currentFolder}/${name}`]),
        [boxID, currentFolder],
    )

    // Данные сервера и флаги чтения отделены от навигации и блокировок изменений.
    const [folderResult, setFolderResult] = useState<{
        boxID: string
        folder: string
        content: FolderContent
    } | null>(null)
    // Ответ хранится вместе с адресом: данные старой папки не выдаём за новые.
    const folderContent =
        folderResult?.boxID === boxID && folderResult.folder === currentFolder
            ? folderResult.content
            : null

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)
    const [refreshCounter, setRefreshCounter] = useState(0)

    const refresh = useCallback(
        () => setRefreshCounter((value) => value + 1),
        [],
    )

    // Эффект перечитывает папку при навигации, подключении и явном refresh.
    // Каждый запуск имеет свой controller, поэтому старый finally не сможет
    // выключить индикатор загрузки уже следующей папки.
    useEffect(() => {
        if (!connected || !token) return

        const controller = new AbortController()

        setLoading(true)
        setError(false)

        fetchFolderContent({
            currentFolder,
            boxID,
            token,
            signal: controller.signal,
        })
            .then((content) => {
                if (!controller.signal.aborted) {
                    rememberFolder(
                        boxID,
                        currentFolder,
                        content.items.map((item) => item.name),
                    )
                    setFolderResult({ boxID, folder: currentFolder, content })
                }
            })
            .catch(() => {
                if (!controller.signal.aborted) setError(true)
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false)
            })

        // Смена папки или уход со страницы делает старый ответ неактуальным.
        return () => controller.abort()
    }, [boxID, connected, currentFolder, refreshCounter, token])

    const create = useCallback(
        async (name: string, signal?: AbortSignal) => {
            if (!token || !connected) throw new FileOperationError('forbidden')

            await createFolder({
                currentFolder,
                folderName: name,
                signal,
                boxID,
                token,
            })
            if (!signal?.aborted) refresh()
        },
        [boxID, currentFolder, refresh, token, connected],
    )

    // Все изменения элемента проходят через одну блокировку. Ключ содержит
    // хранилище и полный путь: одинаковые имена в разных папках не конфликтуют.
    const mutate = useCallback(
        async (name: string, operation: () => Promise<void>) => {
            if (!token || !connected) throw new FileOperationError('forbidden')

            const key = mutationKey(name)
            if (pending.current.has(key))
                throw new FileOperationError('unconfirmed')

            pending.current.add(key)
            setPendingKeys(new Set(pending.current))

            try {
                await operation()
                refresh()
            } finally {
                // Ошибка тоже снимает блокировку: пользователь сможет исправить действие.
                pending.current.delete(key)
                setPendingKeys(new Set(pending.current))
            }
        },
        [mutationKey, refresh, token, connected],
    )
    const removeFolder = useCallback(
        (name: string) =>
            mutate(name, () =>
                deleteFolder({
                    folderPath: `${currentFolder}/${name}`,
                    boxID,
                    token: token!,
                }),
            ),
        [mutate, currentFolder, boxID, token],
    )
    const removeFile = useCallback(
        (name: string) =>
            mutate(name, () =>
                deleteFile({
                    filePath: `${currentFolder}/${name}`,
                    boxID,
                    token: token!,
                }),
            ),
        [mutate, currentFolder, boxID, token],
    )
    const rename = useCallback(
        (oldName: string, newName: string) =>
            mutate(oldName, () =>
                renameFile({
                    filePath: `${currentFolder}/${oldName}`,
                    newFilePath: `${currentFolder}/${newName}`,
                    boxID,
                    token: token!,
                }),
            ),
        [mutate, currentFolder, boxID, token],
    )

    return {
        currentFolder,
        setCurrentFolder,
        folderContent,
        loading: loading || (!folderContent && !error),
        error,
        refresh,
        isAtRoot: currentFolder === '/home',
        createFolder: create,
        deleteFolder: removeFolder,
        deleteFile: removeFile,
        rename,
        isMutating: (name: string) => pendingKeys.has(mutationKey(name)),
    }
}
